import React from 'react'
import { FormattedMessage } from 'react-intl'
import capitalizeString from 'utils/capitalize'
import ActionsMenu from 'features/ActionMenu'
import SortingArrow from 'components/SortingArrow'
import sortDirection from 'constants/sortDirection'
import AssessmentIcon from 'assets/icons/assessments-icon'
import { ProfileContext } from 'providers/profile'
import siteStatuses, { SITE_STATUS_CONFIRMED_ID, SITE_STATUS_EDIT_ONLY_ID, SITE_STATUS_SUBMITTED_EDIT_ONLY_ID, SITE_STATUS_REVIEWED_BY_RFP_ID, SITE_STATUS_SUPERSEDED_ID, SITE_STATUS_DELISTED_ID, SITE_STATUS_DOES_NOT_QUALIFY, SITE_STATUS_PROPOSED_ID, SITE_STATUS_NOMINATED_ID, SITE_STATUS_DRAFT_ID, SITE_STATUS_NOT_ASSESSED_ID } from 'constants/siteStatuses'
import { siteTableActionMenuItems } from 'constants/siteTableActionMenuItems'
import Tooltip from 'components/Tooltip'
import { SortingArrowWrapper } from 'components/SortingArrow/style'
import { INDIVIDUAL_ROLE_ID, NCG_ROLE_ID, RFP_ROLE_ID, SECRETARIAT_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID, BRC_ROLE_ID } from 'constants/roles'
import { getIbaStatusIdForRow, sortAndJoinCriterias } from 'utils/sitesTableHelpers'
import { SitesModalContext } from 'providers/sitesProviders'
import { useIBAAccess } from 'hooks/useIBAAccess'

import {
  SitesTableIconConfirmed, SitesTableIconDelistedWrapper,
  SitesTableUnderAssessmentIcon,
  SitesTableIconDraft,
  SitesTableIconNominated,
  SitesTableIconProposed,
  SitesTableIconReviewedRFP,
  SitesTableIconSuperseded,
  SiteTableCountryContainer,
  SiteTableIconDelisted,
  SitesTableIconsWrapper,
  SiteTableAssessmentIcon, SiteFilterValueWrapper, SitesTableIconNotQualifyWrapper,
  SitesTableIconEditOnly,
  SitesTableIconNotAssessed,
  SitesTableIconSubmittedEdits
} from './style'

const tooltipXOffset = 0
const tooltipYOffset = -16
const tooltipYOffsetAssessment = -15

// Tooltip character limits
const SITE_NAME_MAX_LENGTH = 15
const PROPOSER_MAX_LENGTH = 12
const DEFAULT_MAX_LENGTH = 30

const renderWithTooltip = (text, maxLength = DEFAULT_MAX_LENGTH) => {
  if (!text || text.length <= maxLength) {
    return text
  }

  return (
    <Tooltip
      style={{ zIndex: 1000 }}
      placement='top'
      trigger={['hover']}
      align={{ offset: [tooltipXOffset, tooltipYOffset] }}
      overlay={{ label: text }}
      zIndex="9999"
    >
      <div style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '200px',
        display: 'inline-block'
      }}>
        {text}
      </div>
    </Tooltip>
  )
}

/**
 * Formats country codes using lookup options (no hooks — safe to call per row).
 */
export function formatCountriesForSiteTable(countryArray, countriesOptions) {
  if (!countryArray) {
    return ''
  }
  const formattedArray = countryArray.map((item) => {
    const country = countriesOptions.find((element) => {
      return element.value === item
    })
    return country?.label
  }).filter(Boolean)

  if (countryArray.length > 2) {
    return (
      <SiteTableCountryContainer>
        <Tooltip
          style={{ zIndex: 1000 }}
          placement='top'
          trigger={['hover']}
          align={{ offset: [tooltipXOffset, tooltipYOffset] }}
          overlay={{ label: formattedArray.join(', ') }}
          zIndex="9999"
        >
          <div>
            {formattedArray.slice(0, 2).join(', ')}...
          </div>
        </Tooltip>
      </SiteTableCountryContainer>
    )
  }
  return formattedArray.join(', ')
}

/**
 * Normalize status labels from the API, reports (`kbaStatus` slugs), or site table (`SiteStatus.name`).
 * @param {string|null|undefined} status
 * @returns {string|null}
 */
function normalizeSiteStatusKeyForIcon (status) {
  if (status == null || status === '-') return null
  const s = String(status).trim()
  if (s === '') return null
  return s.toLowerCase()
}

/** Lowercase keys: slugs from reports + capitalized names from Sequelize after toLowerCase */
const SITES_STATUS_ICON_BY_KEY = {
  draft: <SitesTableIconDraft />,
  proposed: <SitesTableIconProposed />,
  nominated: <SitesTableIconNominated />,
  reviewed: <SitesTableIconReviewedRFP />,
  'reviewed by rfp': <SitesTableIconReviewedRFP />,
  confirmed: <SitesTableIconConfirmed />,
  superseded: <SitesTableIconSuperseded />,
  delisted: (
    <SitesTableIconDelistedWrapper>
      <SiteTableIconDelisted />
    </SitesTableIconDelistedWrapper>
  ),
  'does not qualify': <SitesTableIconNotQualifyWrapper />,
  'edit only': <SitesTableIconEditOnly />,
  'submitted edits': <SitesTableIconSubmittedEdits />,
  'not assessed': <SitesTableIconNotAssessed />,
  /** Legacy / alternate slug */
  editonly: <SitesTableIconEditOnly />,
  confirmededitonly: <SitesTableIconSubmittedEdits />
}

export const renderSitesStatusIcon = (status) => {
  const key = normalizeSiteStatusKeyForIcon(status)
  if (key == null) return undefined
  return SITES_STATUS_ICON_BY_KEY[key]
}

export const renderSitesStatusUnderAssessment = (status) => {
  if (status === '-') {
    return
  }

  const statusIcon = {
    Draft:
      <>
        <SitesTableUnderAssessmentIcon />
        <span>Under assessment</span>
      </>,
    Proposed:
      <>
        <SitesTableUnderAssessmentIcon />
        <span>Under assessment</span>
      </>,
    'Reviewed by RFP':
      <>
        <SitesTableUnderAssessmentIcon />
        <span>Under assessment</span>
      </>,
    Nominated:
      <>
        <SitesTableUnderAssessmentIcon />
        <span>Under assessment</span>
      </>,
    Confirmed:
      <>
        <SitesTableIconConfirmed />
        <span>Confirmed</span>
      </>,
    Superseded:
      <>
        <SitesTableIconSuperseded />
        <span>Superseded</span>
      </>,
    Delisted:
      <>
        <SitesTableIconDelistedWrapper>
          <SiteTableIconDelisted />
        </SitesTableIconDelistedWrapper>
        <span>Delisted</span>
      </>,
    'Does not Qualify':
      <SiteFilterValueWrapper>
        <SitesTableIconNotQualifyWrapper />
        Does not Qualify
      </SiteFilterValueWrapper>,
    'Edit Only':
      <>
        <SitesTableIconEditOnly />
        <span>Edit only</span>
      </>,
    'Submitted Edits':
      <>
        <SitesTableIconSubmittedEdits />
        <span>Edits submitted</span>
      </>,
    'Not assessed':
      <>
        <SitesTableIconNotAssessed />
        <span>Not assessed</span>
      </>,
  }

  return statusIcon[status]
}

function getSiteStatusName(siteStatusId) {
  const status = siteStatuses.find((statusItem) => statusItem.id == siteStatusId)
  return status ? status.name : 'Unknown'
}

function renderMySiteAssessmentLabel(siteStatus) {
  if (!siteStatus) {
    return '-'
  }
  if (siteStatus.id === SITE_STATUS_SUPERSEDED_ID) {
    return '-'
  }
  if (siteStatus.id === SITE_STATUS_REVIEWED_BY_RFP_ID && siteStatus.name !== '-') {
    return 'Reviewed by RFP'
  }
  // Text only — icon comes from renderMySiteAssessmentCell (same as Draft/Proposed).
  if (siteStatus.id === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID) {
    return <span>Edits submitted</span>
  }
  if (siteStatus.id === SITE_STATUS_EDIT_ONLY_ID) {
    return <span>Edit only</span>
  }
  return capitalizeString(siteStatus.name)
}

function renderMySiteAssessmentCell(siteStatus) {
  const showIcon = siteStatus && siteStatus.id !== SITE_STATUS_SUPERSEDED_ID
  return (
    <>
      {showIcon && renderSitesStatusIcon(siteStatus.name)}
      {renderMySiteAssessmentLabel(siteStatus)}
    </>
  )
}

function renderNonMySiteStatusCell(site) {
  const ss = site.SiteStatus
  if (!ss) {
    return null
  }
  const label = ss.id === SITE_STATUS_REVIEWED_BY_RFP_ID
    ? 'Reviewed by RFP'
    : capitalizeString(ss.name)
  return (
    <>
      {ss.name && renderSitesStatusIcon(ss.name)}
      {label}
    </>
  )
}

function buildDropItemsProps(site, profile, navigate, setters) {
  const {
    setSiteToWorkWith,
    setSiteForDeleting,
    setSiteIsModalEditOpen,
    setEditOnlySiteToReject,
    setSiteToReject,
    setSiteToPublish,
    setSiteToDelist,
    setIsSiteDoesNotQualified,
    toggleModal,
    setSite,
    approveEditOnlyCallBack,
    isMySite,
    setMonitoringForDeleting,
    setMonitoringForReverting,
  } = setters

  return {
    [INDIVIDUAL_ROLE_ID]: [
      site,
      profile,
      isMySite,
      navigate,
      setSiteToWorkWith,
      setSiteForDeleting,
      toggleModal,
      setSite,
      setSiteIsModalEditOpen,
      setEditOnlySiteToReject,
      setMonitoringForDeleting
    ],
    [NCG_ROLE_ID]: [
      site,
      profile,
      navigate,
      setSiteToWorkWith,
      setSiteForDeleting,
      toggleModal,
      setSite,
      isMySite,
      setMonitoringForDeleting
    ],
    [RFP_ROLE_ID]: [
      site,
      navigate,
      setSiteToWorkWith,
      setSiteToReject,
      toggleModal,
      setSite,
      approveEditOnlyCallBack,
      isMySite,
      setMonitoringForDeleting,
      setMonitoringForReverting,
      profile
    ],
    [SECRETARIAT_ROLE_ID]: [
      site,
      navigate,
      setSiteToWorkWith,
      setSiteToPublish,
      setSiteToDelist,
      setIsSiteDoesNotQualified,
      setSiteForDeleting,
      toggleModal,
      setSite,
      approveEditOnlyCallBack,
      profile,
      isMySite,
      setMonitoringForDeleting,
      setMonitoringForReverting
    ],
    [BIRDLIFE_SECRETARIAT_ROLE_ID]: [
      site,
      navigate,
      setSiteToWorkWith,
      setSiteToPublish,
      setSiteToDelist,
      setIsSiteDoesNotQualified,
      setSiteForDeleting,
      toggleModal,
      setSite,
      approveEditOnlyCallBack,
      profile,
      isMySite,
      setMonitoringForDeleting,
      setMonitoringForReverting
    ],
    [BRC_ROLE_ID]: [
      site,
      navigate,
      setSiteToWorkWith,
      setSiteToReject,
      toggleModal,
      setSite,
      approveEditOnlyCallBack,
      isMySite,
      setMonitoringForDeleting,
      setMonitoringForReverting,
      profile
    ]
  }
}

function getKbaAssessmentStatusForRow(site) {
  if (!site?.reassessmentFlag) {
    return site?.SiteStatus
  }
  return site?.Site?.SiteStatus ?? site?.SiteStatus
}

function renderReassessmentIconMySite(site, siteStatus, showReassessmentListModal) {
  const show = siteStatus && site.SitesHistories && (
    siteStatus.id === SITE_STATUS_CONFIRMED_ID ||
    siteStatus.id === SITE_STATUS_NOT_ASSESSED_ID ||
    siteStatus.id === SITE_STATUS_DELISTED_ID ||
    siteStatus.id === SITE_STATUS_DOES_NOT_QUALIFY
  )
  if (!show) {
    return null
  }
  return (
    <SiteTableAssessmentIcon onClick={() => showReassessmentListModal(site)}>
      <Tooltip
        style={{ zIndex: 1000 }}
        placement='top'
        trigger={['hover']}
        align={{ offset: [tooltipXOffset, tooltipYOffsetAssessment] }}
        overlay={{ label: 'assessmentsHint' }}
        numberOfSites={site.SitesHistories?.length}
        type='small'
      >
        <div>
          <AssessmentIcon />
        </div>
      </Tooltip>
    </SiteTableAssessmentIcon>
  )
}

function renderReassessmentIconAllSites(site, showReassessmentListModal) {
  const ss = site.SiteStatus
  const historiesLen = site.SitesHistories?.length
  const show = historiesLen !== 0 && ss && (
    ss.id === SITE_STATUS_CONFIRMED_ID ||
    ss.id === SITE_STATUS_DELISTED_ID ||
    ss.id === SITE_STATUS_DOES_NOT_QUALIFY ||
    ss.id === SITE_STATUS_NOT_ASSESSED_ID
  )
  if (!show) {
    return null
  }
  return (
    <SiteTableAssessmentIcon onClick={() => showReassessmentListModal(site)}>
      <Tooltip
        style={{ zIndex: 1000 }}
        placement='top'
        trigger={['hover']}
        align={{ offset: [tooltipXOffset, tooltipYOffsetAssessment] }}
        overlay={{ label: 'assessmentsHint' }}
        numberOfSites={site?.SitesHistories?.length}
        type='small'
      >
        <div>
          <AssessmentIcon />
        </div>
      </Tooltip>
    </SiteTableAssessmentIcon>
  )
}

function buildIbaCriteriaValue(site, shouldShowSiteIBAData) {
  const raw = site.ibaCriterias && site.ibaCriterias.length > 0
    ? sortAndJoinCriterias(site.ibaCriterias)
    : null
  return shouldShowSiteIBAData(site, raw, 'sitesManagement')
}

function getSiteMonitoringItem(site) {
  const monitoring = site?.monitoring || site?.Site?.monitoring
  return Array.isArray(monitoring) && monitoring.length > 0 ? monitoring[0] : null
}

function getSiteLastMonitored(site) {
  const item = getSiteMonitoringItem(site)
  const year = item?.monitor_year ?? item?.monitorYear
  return year != null ? year : '-'
}

function renderSiteMonitoringStatus(site) {
  const item = getSiteMonitoringItem(site)
  const status = item?.monitoring_status ?? item?.monitoringStatus
  if (status === SITE_STATUS_DRAFT_ID) {
    return (
      <>
        {renderSitesStatusIcon(siteStatuses?.[SITE_STATUS_DRAFT_ID - 1]?.name?.toLowerCase())}
        {siteStatuses?.[SITE_STATUS_DRAFT_ID - 1]?.name}
      </>
    )
  }
  if (status === SITE_STATUS_CONFIRMED_ID) {
    return (
      <>
        {renderSitesStatusIcon(siteStatuses?.[SITE_STATUS_CONFIRMED_ID - 1]?.name?.toLowerCase())}
        {siteStatuses?.[SITE_STATUS_CONFIRMED_ID - 1]?.name}
      </>
    )
  }
  return '-'
}

function mapSiteToMySiteRow(site, ctx) {
  const {
    profile,
    navigate,
    countriesOptions,
    shouldShowSiteIBAData,
    showReassessmentListModal,
    setters,
  } = ctx

  const dropItemsProps = buildDropItemsProps(site, profile, navigate, setters)
  const dropdownItems = siteTableActionMenuItems[profile.RoleId](...dropItemsProps[profile.RoleId])

  const siteStatus = site.SiteStatus
  const kbaAssessmentStatus = getKbaAssessmentStatusForRow(site)
  const hasIBAAccessForRow = Boolean(site?.ibaAccess?.hasAccess)
  const countryDisplay = formatCountriesForSiteTable(site.SiteDetailsCountryId, countriesOptions)
  const ibaId = getIbaStatusIdForRow(site)

  return {
    id: site.id,
    historyId: site.historyId,
    siteStatusId: site.SiteStatus?.id,
    country: countryDisplay,
    siteId: site.siteCode,
    siteName: renderWithTooltip(site.name, SITE_NAME_MAX_LENGTH),
    year: site.year,
    proposer: renderWithTooltip(site.User ? `${capitalizeString(site.User.lastName || '')} ${capitalizeString(site.User.firstName || '')}` : 'Unknown', PROPOSER_MAX_LENGTH),
    siteStatus: kbaAssessmentStatus
      ? renderSitesStatusUnderAssessment(getSiteStatusName(kbaAssessmentStatus.id))
      : null,
    siteCriteria: sortAndJoinCriterias(site.siteCriterias),
    assessment: renderMySiteAssessmentCell(siteStatus),
    ibaStatus: hasIBAAccessForRow
      ? renderSitesStatusUnderAssessment(
        getSiteStatusName(shouldShowSiteIBAData(site, ibaId, 'sitesManagement'))
      )
      : null,
    ibaCriteria: hasIBAAccessForRow ? buildIbaCriteriaValue(site, shouldShowSiteIBAData) : null,
    lastMonitored: getSiteLastMonitored(site),
    monitoringStatus: renderSiteMonitoringStatus(site),
    actions: (
      <SitesTableIconsWrapper>
        <div style={{ width: '24px' }}>
          {renderReassessmentIconMySite(site, siteStatus, showReassessmentListModal)}
        </div>
        <ActionsMenu dropdownItems={dropdownItems} />
      </SitesTableIconsWrapper>
    ),
  }
}

function mapSiteToAllSitesRow(site, ctx) {
  const {
    profile,
    navigate,
    countriesOptions,
    shouldShowSiteIBAData,
    showReassessmentListModal,
    setters,
  } = ctx

  const dropItemsProps = buildDropItemsProps(site, profile, navigate, setters)
  const dropdownItems = siteTableActionMenuItems[profile.RoleId](...dropItemsProps[profile.RoleId])
  const hasIBAAccessForRow = Boolean(site?.ibaAccess?.hasAccess)
  const countryDisplay = formatCountriesForSiteTable(site.SiteDetailsCountryId, countriesOptions)
  const ibaId = getIbaStatusIdForRow(site)

  return {
    id: site.id,
    historyId: site.historyId,
    siteStatusId: site.SiteStatus?.id,
    country: countryDisplay,
    siteId: site.siteCode,
    siteName: renderWithTooltip(site.name, SITE_NAME_MAX_LENGTH),
    year: site.year,
    proposer: renderWithTooltip(site.User ? `${capitalizeString(site.User.lastName || '')} ${capitalizeString(site.User.firstName || '')}` : 'Unknown', PROPOSER_MAX_LENGTH),
    siteStatus: renderNonMySiteStatusCell(site),
    kbaClassification: site.KbaClassification?.name || '-',
    siteCriteria: sortAndJoinCriterias(site.siteCriterias),
    ibaStatus: hasIBAAccessForRow
      ? renderSitesStatusUnderAssessment(
        getSiteStatusName(shouldShowSiteIBAData(site, ibaId, 'sitesManagement'))
      )
      : null,
    ibaCriteria: hasIBAAccessForRow ? buildIbaCriteriaValue(site, shouldShowSiteIBAData) : null,
    lastMonitored: getSiteLastMonitored(site),
    monitoringStatus: renderSiteMonitoringStatus(site),
    actions: (
      <SitesTableIconsWrapper>
        <div style={{ width: '24px' }}>
          {renderReassessmentIconAllSites(site, showReassessmentListModal)}
        </div>
        <ActionsMenu dropdownItems={dropdownItems} />
      </SitesTableIconsWrapper>
    ),
  }
}

/**
 * Row factory for Sites table. Hooks must run in the parent — pass `rowContext` from there.
 * @param sitesTableCallbacks — modal/setter callbacks (keeps arity low for S107).
 * @param sites — last parameter so default `[]` satisfies default-param-last.
 */
export const makeSitesRows = (sitesTableCallbacks, rowContext, sites = []) => {
  const {
    setSiteToWorkWith,
    setSiteToReject,
    setSiteToPublish,
    setSiteToDelist,
    showReassessmentListModal,
    isMySite,
    setSiteForDeleting,
    setIsSiteDoesNotQualified,
    setSiteIsModalEditOpen,
    approveEditOnlyCallBack,
    setEditOnlySiteToReject,
    setMonitoringForDeleting,
    setMonitoringForReverting
  } = sitesTableCallbacks

  const {
    profile,
    navigate,
    toggleModal,
    setSite,
    setIsLoading,
    countriesOptions,
    shouldShowSiteIBAData,
  } = rowContext

  if (!profile?.id) {
    return []
  }

  if (!Array.isArray(sites)) {
    setIsLoading(false)
    return []
  }

  const setters = {
    setSiteToWorkWith,
    setSiteForDeleting,
    setSiteIsModalEditOpen,
    setEditOnlySiteToReject,
    setSiteToReject,
    setSiteToPublish,
    setSiteToDelist,
    setIsSiteDoesNotQualified,
    toggleModal,
    setSite,
    approveEditOnlyCallBack,
    isMySite,
    setMonitoringForDeleting,
    setMonitoringForReverting,
  }

  const ctx = {
    profile,
    navigate,
    countriesOptions,
    shouldShowSiteIBAData,
    showReassessmentListModal,
    setters,
  }

  const sitesNumber = sites.length
  let countSites = 0

  return sites.map((site) => {
    countSites++
    if (countSites === sitesNumber) {
      setIsLoading(false)
    }
    return isMySite ? mapSiteToMySiteRow(site, ctx) : mapSiteToAllSitesRow(site, ctx)
  })
}

export const makeSitesColumns = (sorting, setSorting, isMySite, profile, canSeeIBASitesManagement) => {
  const sortFunction = (sortBy) => {
    if (sorting.sortBy === sortBy) {
      if (sorting.sortDirection === sortDirection.ASC) {
        setSorting({ sortBy: sortBy, sortDirection: sortDirection.DESC })
      } else {
        setSorting({ sortBy: sortBy, sortDirection: sortDirection.ASC })
      }
    } else {
      setSorting({ sortBy: sortBy, sortDirection: sortDirection.ASC })
    }
  }

  const sortBySiteName = () => sortFunction('name')
  const sortByYear = () => sortFunction('year')
  const sortBySiteCode = () => sortFunction('siteCode')
  const sortByProposer = () => sortFunction('ProposerId')
  const sortBySiteStatus = () => sortFunction('SiteStatusId')
  const sortBySiteAssessmentStatus = () => sortFunction('SiteStatusId')

  const headers = [
    {
      id: 'country',
      header: <FormattedMessage id='country' />,
      accessorKey: 'country',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'siteName',
      header: (
        <SortingArrowWrapper>
          <FormattedMessage id='siteName' />
          <SortingArrow
            onChange={sortBySiteName}
            checked={sorting.sortBy === 'name' && sorting.sortDirection === sortDirection.ASC}
          />
        </SortingArrowWrapper>
      ),
      accessorKey: 'siteName',
      size: 15,
      style: {
        maxWidth: '400px',
        minWidth: '60px',
        width: '250px'
      },
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'siteId',
      header: (
        <SortingArrowWrapper>
          <FormattedMessage id='siteId' />
          <SortingArrow
            onChange={sortBySiteCode}
            checked={sorting.sortBy === 'siteCode' && sorting.sortDirection === sortDirection.ASC}
          />
        </SortingArrowWrapper>
      ),
      accessorKey: 'siteId',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'year',
      header: (
        <SortingArrowWrapper>
          <FormattedMessage id='year' />
          <SortingArrow
            onChange={sortByYear}
            checked={sorting.sortBy === 'year' && sorting.sortDirection === sortDirection.ASC}
          />
        </SortingArrowWrapper>
      ),
      accessorKey: 'year',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'proposer',
      header: (
        <SortingArrowWrapper>
          <FormattedMessage id='lastProposer' />
          <SortingArrow
            onChange={sortByProposer}
            checked={sorting.sortBy === 'ProposerId' && sorting.sortDirection === sortDirection.ASC}
          />
        </SortingArrowWrapper>
      ),
      accessorKey: 'proposer',
      style: {
        maxWidth: '300px',
        minWidth: '100px',
        width: '150px'
      },
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'siteStatus',
      header: (
        <SortingArrowWrapper>
          <FormattedMessage id='kbaStatus' />
          <SortingArrow
            onChange={sortBySiteStatus}
            checked={sorting.sortBy === 'SiteStatusId' && sorting.sortDirection === sortDirection.ASC}
          />
        </SortingArrowWrapper>
      ),
      accessorKey: 'siteStatus',
      style: {
        maxWidth: '300px',
        minWidth: '150px',
        width: '200px'
      },
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'siteCriteria',
      header: <FormattedMessage id='kbaCriteria' />,
      accessorKey: 'siteCriteria',
      cell: ({ getValue }) => getValue()
    },
    ...(canSeeIBASitesManagement ? [
      {
        id: 'ibaStatus',
        header: <FormattedMessage id='ibaStatus' />,
        accessorKey: 'ibaStatus',
        cell: ({ getValue }) => getValue()
      },
      {
        id: 'ibaCriteria',
        header: <FormattedMessage id='ibaCriteria' />,
        accessorKey: 'ibaCriteria',
        cell: ({ getValue }) => getValue()
      }
    ] : []),
    ...(!isMySite
      ? [
          {
            id: 'lastMonitored',
            header: <FormattedMessage id="Last Monitored" />,
            accessorKey: 'lastMonitored',
            style: {
              maxWidth: '300px',
              minWidth: '150px',
              width: '200px'
            },
            cell: ({ getValue }) => getValue()
          }
        ]
      : []),
    {
      id: 'actions',
      header: <FormattedMessage id='actions' />,
      accessorKey: 'actions',
      style: {
        width: '5%'
      },
      cell: ({ getValue }) => getValue()
    }
  ]

  if (isMySite) {
    const actionsIndex = headers.findIndex((header) => header.accessorKey === 'actions')
    const insertIndex = actionsIndex === -1 ? headers.length : actionsIndex

    headers.splice(
      insertIndex,
      0,
      {
        id: 'assessment',
        header: (
          <SortingArrowWrapper>
            <FormattedMessage id="assessment" />
            <SortingArrow
              onChange={sortBySiteAssessmentStatus}
              checked={sorting.sortBy === 'SiteStatusId' && sorting.sortDirection === sortDirection.ASC}
            />
          </SortingArrowWrapper>
        ),
        accessorKey: 'assessment',
        cell: ({ getValue }) => getValue()
      },
      {
        id: 'lastMonitored',
        header: <FormattedMessage id="lastMonitored" />,
        accessorKey: 'lastMonitored',
        style: {
          maxWidth: '300px',
          minWidth: '150px',
          width: '200px'
        },
        cell: ({ getValue }) => getValue()
      },
      {
        id: 'monitoringStatus',
        header: <FormattedMessage id="monitoringStatus" />,
        accessorKey: 'monitoringStatus',
        style: {
          maxWidth: '300px',
          minWidth: '150px',
          width: '200px'
        },
        cell: ({ getValue }) => getValue()
      }
    )
  } else {
    const kbaStatusIndex = headers.findIndex((header) => header.accessorKey === 'siteStatus')
    if (kbaStatusIndex !== -1) {
      headers.splice(kbaStatusIndex + 1, 0, {
        id: 'kbaClassification',
        header: <FormattedMessage id="kbaClassification" />,
        accessorKey: 'kbaClassification',
        style: {
          maxWidth: '300px',
          minWidth: '150px',
          width: '200px'
        },
        cell: ({ getValue }) => getValue()
      })
    }
  }

  return headers
}
